import { useEffect, useState } from 'react';
import { Calendar, Plus, Pencil, Trash2, Loader2, X, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface Event {
  id: number;
  title: string;
  date: string;
  time: string;
  location: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  meals: number;
  volunteers: number;
  sortOrder: number;
  isActive: number;
  createdAt: string;
}

const emptyForm = {
  title: '',
  date: '',
  time: '',
  location: '',
  status: 'upcoming' as Event['status'],
  meals: 0,
  volunteers: 0,
  sortOrder: 0,
  isActive: 1,
};

const statusLabels: Record<Event['status'], string> = {
  upcoming: '即將舉行',
  ongoing: '進行中',
  completed: '已完成',
  cancelled: '已取消',
};

const statusColors: Record<Event['status'], string> = {
  upcoming: 'bg-blue-50 text-blue-700',
  ongoing: 'bg-green-50 text-green-700',
  completed: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-50 text-red-700',
};

export default function AdminEvents() {
  const [items, setItems] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    const res = await apiFetch<Event[]>('/api/admin/events');
    if (res.success && res.data) {
      setItems(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item: Event) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      date: item.date,
      time: item.time || '',
      location: item.location,
      status: item.status,
      meals: item.meals || 0,
      volunteers: item.volunteers || 0,
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.date.trim() || !form.location.trim()) {
      alert('請輸入活動名稱、日期和地點');
      return;
    }
    setSaving(true);
    const url = editingId
      ? `/api/admin/events/${editingId}`
      : '/api/admin/events';
    const method = editingId ? 'PUT' : 'POST';
    const res = await apiFetch(url, {
      method,
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (res.success) {
      setModalOpen(false);
      fetchItems();
    } else {
      alert(res.error?.message || '保存失敗');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除這個活動嗎？')) return;
    const res = await apiFetch(`/api/admin/events/${id}`, { method: 'DELETE' });
    if (res.success) {
      fetchItems();
    } else {
      alert(res.error?.message || '刪除失敗');
    }
  };

  const toggleActive = async (item: Event) => {
    const res = await apiFetch(`/api/admin/events/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: item.isActive ? 0 : 1 }),
    });
    if (res.success) fetchItems();
  };

  const moveOrder = async (item: Event, direction: 'up' | 'down') => {
    const currentIndex = items.findIndex((i) => i.id === item.id);
    if (currentIndex === -1) return;
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    const targetItem = items[targetIndex];

    await Promise.all([
      apiFetch(`/api/admin/events/${item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ sortOrder: targetItem.sortOrder }),
      }),
      apiFetch(`/api/admin/events/${targetItem.id}`, {
        method: 'PUT',
        body: JSON.stringify({ sortOrder: item.sortOrder }),
      }),
    ]);
    fetchItems();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-brand-600" />
          社區活動管理
        </h1>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold py-2.5 px-5 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          新增活動
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="px-4 py-3 text-left font-medium w-10">排序</th>
                <th className="px-4 py-3 text-left font-medium">活動名稱</th>
                <th className="px-4 py-3 text-left font-medium">日期</th>
                <th className="px-4 py-3 text-left font-medium">時間</th>
                <th className="px-4 py-3 text-left font-medium">地點</th>
                <th className="px-4 py-3 text-left font-medium">狀態</th>
                <th className="px-4 py-3 text-center font-medium">義工數</th>
                <th className="px-4 py-3 text-center font-medium">顯示/隱藏</th>
                <th className="px-4 py-3 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <span className="inline-block w-full h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : items.length > 0 ? (
                items.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveOrder(item, 'up')}
                          disabled={index === 0}
                          className="text-gray-400 hover:text-brand-600 disabled:opacity-30 disabled:hover:text-gray-400"
                          title="上移"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => moveOrder(item, 'down')}
                          disabled={index === items.length - 1}
                          className="text-gray-400 hover:text-brand-600 disabled:opacity-30 disabled:hover:text-gray-400"
                          title="下移"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.title}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.date}</td>
                    <td className="px-4 py-3 text-gray-600">{item.time || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{item.location}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[item.status]}`}>
                        {statusLabels[item.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{item.volunteers || 0}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleActive(item)}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                          item.isActive
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={item.isActive ? '顯示中' : '已隱藏'}
                      >
                        {item.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(item)}
                          className="p-2 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                          title="編輯"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="刪除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-400">
                    暫無社區活動，點擊「新增活動」開始添加
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {editingId ? '編輯活動' : '新增活動'}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">活動名稱 *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="例如：社區義工探訪日"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">日期 *</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">時間</label>
                  <input
                    type="text"
                    value={form.time}
                    onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="例如：09:00 - 17:00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">地點 *</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="例如：深水埗社區中心"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">狀態</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as Event['status'] }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm bg-white"
                >
                  <option value="upcoming">即將舉行</option>
                  <option value="ongoing">進行中</option>
                  <option value="completed">已完成</option>
                  <option value="cancelled">已取消</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">供餐數</label>
                  <input
                    type="number"
                    value={form.meals}
                    onChange={(e) => setForm((f) => ({ ...f, meals: Number(e.target.value) }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">義工數</label>
                  <input
                    type="number"
                    value={form.volunteers}
                    onChange={(e) => setForm((f) => ({ ...f, volunteers: Number(e.target.value) }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">排序</label>
                  <input
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  />
                </div>
                <div className="flex items-center gap-2 pt-7">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={!!form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked ? 1 : 0 }))}
                    className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    前台顯示
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold py-2.5 px-5 rounded-xl transition-colors disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
